import React from 'react';
import { CustomIcon, type CustomIconName } from '../CustomIcon/CustomIcon';
import './VideoButton.scss';

/**
 * VideoButton — visual play-circle affordance.
 *
 * This is a pure presentation primitive — it renders the dark-circle + white-
 * triangle visual and nothing else. It carries no `<button>` semantics and no
 * onClick handler; consumers wrap it in whatever interactive element they
 * need (a `<button>` to open a lightbox, an `<a>` to navigate, or none at all
 * inside another clickable region). The "Button" in the name describes the
 * visual affordance, not the HTML element.
 *
 * Used internally by `<Video>` (see `playButtonSize` prop). Can also be
 * composed by any consumer that wants the play-circle visual on its own
 * thumbnail.
 */

export type VideoButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export interface VideoButtonProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * Visual size variant.
   * - `'xs'` — 40px (compact corner control, e.g. MediaSection background video)
   * - `'sm'` — 48px (compact thumbnails)
   * - `'md'` — 72px (default; featured cards, MegaMenu)
   * - `'lg'` — 90px (hero / full-bleed)
   */
  size?: VideoButtonSize;

  /**
   * Glyph rendered inside the circle. Defaults to the play triangle; pass
   * `'pause'` to use the same circle as a play/pause toggle control.
   */
  icon?: CustomIconName;
}

export const VideoButton: React.FC<VideoButtonProps> = ({
  size = 'md',
  icon = 'play',
  className,
  ...rest
}) => {
  const classes = [
    'poly-video-button',
    `poly-video-button--${size}`,
    `poly-video-button--icon-${icon}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} aria-hidden="true" {...rest}>
      <CustomIcon name={icon} aria-hidden="true" />
    </span>
  );
};
